import { ApolloClient, DefaultContext, useMutation, useQuery } from '@apollo/client'
import { CircularProgress } from '@material-ui/core'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

import {
  AccountDocumentType,
  SearchAccountDocumentDocument,
  UploadAccountDocumentDocument,
  UploadAccountDocumentInput,
} from '../../../../../../graphql/search'
import useLogAndCaptureError from '../../../../../../hooks/useLogAndCaptureError'
import { ApolloClientsContext } from '../../../../../../lib/graphql/apollo'
import { PractitionerSettings } from '../../../../../../lib/practitioner-settings/types'
import { logAndCaptureException } from '../../../../../../utils/errorTools'
import { checkQueryResponse } from '../../../../../../utils/graphqlTools'
import { MutationErrorAlert } from '../../../../../MutationErrorAlert'
import { FileSubmitted } from '../FileSubmitted'
import { FileUpload } from '../FileUpload'
import * as Styled from './styled'

export interface AccountDocumentFormProps {
  documentType: AccountDocumentType
  settings: PractitionerSettings
}

interface FormValues {
  documentUrl: string
  fileName: string
}

export const AccountDocumentForm: React.FC<AccountDocumentFormProps> = ({
  documentType,
  settings,
}) => {
  const { comma: commaClient } = useContext(ApolloClientsContext)

  const [uploadAccountDocument, { loading: uploadAccountDocumentLoading }] = useMutation(
    UploadAccountDocumentDocument,
    {
      client: commaClient as ApolloClient<object>,
      context: { useLatestTokenIfLoggedIn: true } as DefaultContext,
    },
  )
  const saveDisabled = uploadAccountDocumentLoading

  const methods = useForm<FormValues>()
  const { formState, handleSubmit, watch, setValue, register, reset, setError, errors } = methods
  const currentFileName = watch('fileName')

  const onSubmit: SubmitHandler<FormValues> = async ({ documentUrl, fileName }) => {
    const input: UploadAccountDocumentInput = {
      documentType,
      documentUrl,
      fileName,
    }
    const result = await uploadAccountDocument({
      variables: { input },
    })
      .then(checkQueryResponse)
      .then(() => {
        commaClient.cache.evict({ id: 'ROOT_QUERY', fieldName: 'customer' })
      })
      .then(() => ({ error: undefined }))
      .catch((err) => {
        const error = err instanceof Error ? err : new Error(`${err}`)
        logAndCaptureException(error)
        return { error }
      })
    if (result.error) {
      setError('fileName', { message: result.error.message, type: 'server' })
    }
  }

  const onFileUpload = useCallback(
    (details: { documentUrl: string; fileName: string }) => {
      setValue('documentUrl', details.documentUrl, { shouldDirty: true })
      setValue('fileName', details.fileName, { shouldDirty: true })
    },
    [setValue],
  )

  const { data, error: searchAccountDocumentError } = useQuery(SearchAccountDocumentDocument, {
    client: commaClient,
    context: { useLatestTokenIfLoggedIn: true } as DefaultContext,
  })
  useLogAndCaptureError(searchAccountDocumentError)
  const [accountDocument, defaultValues] = useMemo(() => {
    const document = data?.customer?.accountDocument?.find(
      (doc) => !!doc && doc.documentType === documentType,
    )
    const formValues: FormValues = document
      ? {
          documentUrl: '',
          fileName: document.fileName,
        }
      : documentType === AccountDocumentType.W9TaxId && settings.practitioner.taxIdFormReceived
        ? {
            documentUrl: '',
            fileName: 'W-9 Taxpayer Identification Form',
          }
        : documentType === AccountDocumentType.TaxExemption && !settings.practitioner.isTaxable
          ? {
              documentUrl: '',
              fileName: 'Tax Exemption Form',
            }
          : {
              documentUrl: '',
              fileName: '',
            }
    return [document, formValues]
  }, [data, documentType, settings])

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  const hasServerError = !!errors.fileName && errors.fileName.type === 'server'

  return (
    <FormProvider {...methods}>
      <Styled.Form onSubmit={handleSubmit(onSubmit)}>
        <input type="hidden" name="documentUrl" ref={register} />
        <input type="hidden" name="fileName" ref={register} />
        {hasServerError && (
          <MutationErrorAlert
            onCancel={() => reset(defaultValues)}
            onRetry={handleSubmit(onSubmit)}
          >
            An error occurred uploading your file, and we have been notified.
          </MutationErrorAlert>
        )}
        {currentFileName ? (
          <FileSubmitted
            fileName={currentFileName}
            isDirty={formState.isDirty}
            updatedAt={accountDocument?.updatedAt}
            documentType={documentType}
          />
        ) : (
          <FileUpload onFileUpload={onFileUpload} />
        )}
        {formState.isDirty && (
          <Styled.Buttons>
            <Styled.SaveButton type="submit" disabled={formState.isSubmitting || saveDisabled}>
              {formState.isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Save'}
            </Styled.SaveButton>
            <Styled.CancelButton
              disabled={formState.isSubmitting}
              onClick={() => reset(defaultValues)}
            >
              Cancel
            </Styled.CancelButton>
          </Styled.Buttons>
        )}
      </Styled.Form>
    </FormProvider>
  )
}
